import { formatDate } from "app/main/utils/dateUtils";
import { authRoles } from "app/main/consts";
import { isEnabled } from "app/services/featureStorageService";
import { convertRelayScheduleChainToSearch, saveSearchesSessionStorage } from "app/main/searchV3/utils/searchUtils";

import Typography from "@material-ui/core/Typography";
import Icon from "@material-ui/core/Icon";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Tooltip from "@material-ui/core/Tooltip";

import {
	LocalShipping as TruckIcon,
	Schedule as ScheduleIcon,
	CheckCircle as CompletedIcon,
	Warning as WarningIcon,
	Info as InfoIcon,
	PauseCircleFilled as DeferredIcon,
	CheckCircleOutline as ForcedIcon,
	SwapHoriz as SwapIcon,
	FileCopy as ConflictIcon,
	ExitToApp as OutsideIcon,
	EventBusy as EventBusyIcon,
	Cancel as CancelIcon,
} from "@material-ui/icons";

import moment from "moment";
import TruckCellView from "@smarthop/list/views/TruckCellView";
import { openFormDialog } from "app/store/tools/formDialogSlice";
import { createRequest, createHeaders, throwResErrors } from "app/services/requestUtil";
import { showSnackbar } from "app/main/utils/snackbarUtil";

const PROD_DB = process.env.REACT_APP_ENV === "PRD";
const FORCED_TOOLTIP = "Forced child trip (outside schedule)";

// Helper function to handle search for loads
const handleSearchForLoads = async (chain, history, dispatch) => {
	try {
		const searchFilters = convertRelayScheduleChainToSearch(chain);
		await saveSearchesSessionStorage(searchFilters);
		history.push("/search?id=EMPTY");
	} catch (error) {
		console.error("Error launching search:", error);
	}
};

// Helper function to update truck plan and then start the agent
const handleStartAgent = async (chain, dispatch, snackbar) => {
	try {
		const carrierId = chain.carrier || chain.pickup_schedules?.[0]?.carrier_id;
		const truckId = chain.truck?._id;

		if (!carrierId || !truckId) {
			showSnackbar(snackbar, "Missing carrier or truck information", "error");
			return;
		}

		// First, update the truck plan with the chain data
		const request = createRequest();
		const config = { headers: createHeaders() };
		const url = `api/profile/carriers/${carrierId}/relay-schedules/chains/update-truck-plan`;

		const response = await request
			.post(url, { data: chain }, config)
			.then((res) => res.data)
			.catch(throwResErrors);

		showSnackbar(snackbar, "Truck plan updated, opening agent configuration...", "success");

		// Get the plan ID from the response or use the existing one
		const planId = response?.data?.planId || chain.truck?.plan;

		// Extract origin from pickup schedules
		const originLocations = (chain.pickup_schedules || [])
			.map((s) => {
				if (s.origin_yard?.search_city && !s.was_adjusted && !s.covered_trips?.length) {
					return `${s.origin_yard.search_city}, ${s.origin_yard.search_state}`;
				}
				return s.origin_yard ? `${s.origin_yard.city}, ${s.origin_yard.state}` : null;
			})
			.filter(Boolean);

		// Find the earliest pickup schedule start time
		let earliestPickup = null;
		if (chain.pickup_schedules && chain.pickup_schedules.length > 0) {
			chain.pickup_schedules.forEach((schedule) => {
				const startTime = moment(schedule.start_datetime);
				if (!earliestPickup || startTime.isBefore(earliestPickup)) {
					earliestPickup = startTime;
				}
			});
		}

		// Open the Agent Configuration dialog
		dispatch(
			openFormDialog({
				viewId: "AGENT_CONFIGURATION_VIEW",
				dataIds: {
					carrierId: carrierId,
					truckId: truckId,
					planId: planId,
					chainId: chain.id,
					searchMode: "relay_chain",
					truckInfo: {
						truckId: chain.truck__view?.label || chain.truck?.truckid || truckId,
						carrierName: chain.carrier__view?.label || "Carrier",
						currentLocation: originLocations[0] || "Unknown",
						availableDate: earliestPickup ? earliestPickup.startOf("day").toISOString() : null,
					},
				},
			})
		);
	} catch (error) {
		console.error("Error starting agent:", error);
		const errorMessage = error?.errors?.[0]?.message || error?.message || "Failed to update truck plan";
		showSnackbar(snackbar, errorMessage, "error");
	}
};

// Helper function to format date/time
const formatDateTime = (dateTime) => {
	if (!dateTime) return "N/A";
	return moment(dateTime).format("ddd MM/DD HH:mm");
};

const formatDateOnly = (dateTime) => {
	if (!dateTime) return "N/A";
	return moment(dateTime).format("ddd MM/DD");
};

const formatTime = (dateTime) => {
	if (!dateTime) return "N/A";
	return moment(dateTime).format("HH:mm");
};

const DEFERRED_COLOR = "#e65100";

const DEFERRED_REASON_META = {
	MANUAL_DEFERRED: { icon: DeferredIcon, color: "#7b1fa2", label: "Manual" },
	NO_PICKUP_TRUCK_ELIGIBLE: { icon: DeferredIcon, color: "#e65100", label: "Pickup trucks unavailable" },
	NO_PICKUP_TRUCK: { icon: DeferredIcon, color: "#e65100", label: "No pickup truck" },
	NO_LONGHAUL_TRUCK_ELIGIBLE: { icon: DeferredIcon, color: "#e65100", label: "Longhaul trucks unavailable" },
	NO_LONGHAUL_TRUCK: { icon: DeferredIcon, color: "#e65100", label: "No longhaul truck" },
	NO_DELIVERY_TRUCK_ELIGIBLE: { icon: DeferredIcon, color: "#e65100", label: "Delivery trucks unavailable" },
	NO_DELIVERY_TRUCK: { icon: DeferredIcon, color: "#e65100", label: "No delivery truck" },
	SCHEDULE_NOT_ACTIVE: { icon: DeferredIcon, color: "#e65100", label: "Not active today" },
	INCOMPLETE_CHAIN: { icon: DeferredIcon, color: "#e65100", label: "Incomplete chain" },
	INCOMPLETE_INSTANCES: { icon: DeferredIcon, color: "#e65100", label: "Incomplete instances" },
	SCHEDULE_CONFLICT: { icon: DeferredIcon, color: "#e65100", label: "Schedule conflict" },
	COVERED_BY_ACTIVE_CHAIN: { icon: DeferredIcon, color: "#e65100", label: "Covered by active trip" },
	ERROR: { icon: DeferredIcon, color: "#e65100", label: "Error" },
};

const OUTSIDE_SCHEDULE_REASON_LABELS = {
	NO_TRUCK_TRIPS: "No Active Trips",
	DATE_MISMATCH: "Date Mismatch",
	TYPE_MISMATCH: "Type Mismatch",
	YARD_FILTER: "Yard Filter",
	DIFFERENT_LOAD: "Different Load",
	YARD_MISMATCH: "Yard Mismatch",
};

// Sub-reason labels for POSSIBLE_CANCELLATION (carried in warning.metadata.reason).
// Mirrors the backend reason codes set in relayScheduleManager.js when emitting
// the warning, so we can show why the chain may need cancellation rather than
// just a generic label.
const POSSIBLE_CANCELLATION_REASON_LABELS = {
	LONGHAUL_TRUCK_NOT_AT_YARD: "LH truck not at yard",
	ASSIGNED_TRUCK_UNAVAILABLE: "Assigned truck unavailable",
};

const WARNING_REASON_META = {
	LONGHAUL_NOT_PARTNER: { icon: SwapIcon, color: "#0277bd", label: "LineHaul Mismatch" },
	TRUCK_CONFLICT_SAME_POSITION: { icon: ConflictIcon, color: "#c62828", label: "Truck Conflict" },
	CHILD_TRIP_OUTSIDE_SCHEDULE: { icon: OutsideIcon, color: "#ef6c00", label: "Outside Schedule" },
	POSSIBLE_CANCELLATION: { icon: CancelIcon, color: "#d32f2f", label: "Possible Cancellation" },
	NON_ACTIVE_RELAY_TRUCK: { icon: EventBusyIcon, color: "#6a1b9a", label: "Non-Relay Truck" },
};

const getDeferredReasonMeta = (reasonCode) => DEFERRED_REASON_META[reasonCode] || null;
const getWarningMeta = (warningCode) => WARNING_REASON_META[warningCode] || null;

// Format the POSSIBLE_CANCELLATION metadata into a concise sub-label and an
// expanded tooltip block. Returns { subLabel, tooltipDetail } where either may
// be null when the warning has no metadata. ASSIGNED_TRUCK_UNAVAILABLE carries
// per-position assignments (PICKUP / LONGHAUL / DELIVERY) so the user can see
// exactly which slot is on an unavailable truck.
const formatPossibleCancellationMetadata = (warning) => {
	const reason = warning?.metadata?.reason;
	const reasonLabel = reason ? POSSIBLE_CANCELLATION_REASON_LABELS[reason] || reason.replace(/_/g, " ") : null;
	const assignments = Array.isArray(warning?.metadata?.unavailable_assignments)
		? warning.metadata.unavailable_assignments
		: [];
	const assignmentsLabel = assignments.length
		? assignments.map((a) => `${a.position} #${a.truckLabel || a.truckId}${a.date ? ` on ${a.date}` : ""}`).join(", ")
		: null;
	let subLabel = null;
	if (reason === "ASSIGNED_TRUCK_UNAVAILABLE") {
		subLabel = assignmentsLabel ? `Unavailable: ${assignmentsLabel}` : reasonLabel;
	} else if (reasonLabel) {
		subLabel = reasonLabel;
	}
	const tooltipDetailParts = [];
	if (reasonLabel) tooltipDetailParts.push(reasonLabel);
	if (assignmentsLabel) tooltipDetailParts.push(assignmentsLabel);
	return {
		subLabel,
		tooltipDetail: tooltipDetailParts.length ? tooltipDetailParts.join(" — ") : null,
	};
};

const getStatusIcon = (status, reason, reasonCode) => {
	switch (status) {
		case "COMPLETED":
		case "COVERED":
			return <CompletedIcon style={{ color: "#4caf50", fontSize: "1rem", marginRight: 4 }} />;
		case "PARTIALLY_COVERED":
			return <WarningIcon style={{ color: "#ff9800", fontSize: "1rem", marginRight: 4 }} />;
		case "AVAILABLE":
			return <ScheduleIcon style={{ color: "#2196f3", fontSize: "1rem", marginRight: 4 }} />;
		case "URGENT":
			return <WarningIcon style={{ color: "#f44336", fontSize: "1rem", marginRight: 4 }} />;
		case "DEFERRED": {
			const meta = getDeferredReasonMeta(reasonCode);
			const IconComponent = meta?.icon || DeferredIcon;
			const iconColor = meta?.color || DEFERRED_COLOR;
			const tooltipText = reason || (meta ? meta.label : "Chain deferred — resources unavailable");
			return (
				<Tooltip title={tooltipText}>
					<IconComponent style={{ color: iconColor, fontSize: "1rem", marginRight: 4 }} />
				</Tooltip>
			);
		}
		default:
			return <InfoIcon style={{ color: "#9e9e9e", fontSize: "1rem", marginRight: 4 }} />;
	}
};

// Helper function to determine chain status
const getChainStatus = (chain) => {
	// Check if chain is deferred (missing trucks or resources)
	if (chain.coverage_status === "DEFERRED" || chain.is_deferred) {
		return "DEFERRED";
	}

	// Check if any schedule in the chain has overlap with active trips
	const hasOverlap =
		chain.pickup_schedules?.some((s) => s.has_overlap) ||
		chain.longhaul_schedules?.some((s) => s.has_overlap) ||
		chain.delivery_schedules?.some((s) => s.has_overlap);

	// Check if this is an active trip chain (has covering trips but no schedules)
	const isActiveChain = chain.coverage_status === "COMPLETED" || chain.chain_type === "ACTIVE_TRIP_CHAIN" || hasOverlap;

	if (isActiveChain) {
		return "COMPLETED";
	} else if (chain.urgency === "CRITICAL" || chain.urgency === "HIGH") {
		return "URGENT";
	} else {
		return "AVAILABLE";
	}
};

// Helper function to render schedule chips
const getChipTruckLabel = (schedule) => {
	const covered = schedule.covered_trips?.[0]?.truck?.truckid;
	if (covered) return `#${covered} `;
	const assigned = schedule.truck?.truckid || schedule.assigned_truck?.truckid;
	return assigned ? `#${assigned} ` : "";
};

const appendPlannedTruckTooltip = (schedule, tooltipText) => {
	const coveredTruckId = schedule.covered_trips?.[0]?.truck?.truckid;
	if (
		schedule.original_assigned_truck_truckid &&
		coveredTruckId &&
		coveredTruckId !== schedule.original_assigned_truck_truckid
	) {
		return `${tooltipText}\n📋 Planned truck: #${schedule.original_assigned_truck_truckid}`;
	}
	return tooltipText;
};

const renderScheduleChips = (chain, dispatch, carrierId) => {
	const schedules = [];

	const openTripView = (trip) => {
		if (!dispatch) return;
		const tripId = trip.trip_id || trip._id;
		const tripCarrierId = trip.carrier || carrierId;
		if (!tripId) return;
		dispatch(openFormDialog({ viewId: "TRIP_VIEW", dataIds: { tripId, carrierId: tripCarrierId } }));
	};

	const openTruckView = (schedule) => {
		if (!dispatch) return;
		const truckId = schedule.assigned_truck?._id || schedule.truck?._id || schedule.truck_id;
		const truckCarrierId = carrierId;
		if (!truckId || !truckCarrierId) return;
		dispatch(openFormDialog({ viewId: "TRUCK_DETAILS_VIEW", dataIds: { carrierId: truckCarrierId, truckId } }));
	};

	// For active trip chains, show trip information instead of schedules
	if (chain.chain_type === "ACTIVE_TRIP_CHAIN" && chain.covering_trips?.length > 0) {
		chain.covering_trips.forEach((trip, index) => {
			const isForced = trip.outside_schedule === true;
			const tooltipTitle = `Active Trip: ${trip.load_id} - ${trip.pickup_address} → ${trip.delivery_address}${
				isForced ? `\n⚠️ ${FORCED_TOOLTIP}` : ""
			}`;
			schedules.push(
				<Tooltip key={`trip-${index}`} title={tooltipTitle}>
					<Chip
						size="small"
						label={<>{`Trip: ${trip.load_id}${isForced ? " (Forced)" : ""}`}</>}
						style={{
							backgroundColor: isForced ? "#fff3e0" : "#e8f5e8",
							color: isForced ? "#e65100" : "#2e7d32",
							margin: "2px",
							fontSize: "0.75rem",
							cursor: dispatch && (trip.trip_id || trip._id) ? "pointer" : "default",
						}}
						icon={<CompletedIcon style={{ fontSize: "0.8rem" }} />}
						onClick={dispatch && (trip.trip_id || trip._id) ? () => openTripView(trip) : undefined}
					/>
				</Tooltip>
			);
		});
		return schedules;
	}

	// Add pickup schedules
	if (chain.pickup_schedules?.length > 0) {
		chain.pickup_schedules.forEach((schedule, index) => {
			// Build tooltip with adjustment reason if available
			let tooltipText = `${schedule.truck?.truckid ? `#${schedule.truck?.truckid} ` : ""}${
				schedule.truck__view?.label ? `${schedule.truck__view?.label} ` : ""
			}P: ${schedule.schedule_name} (${formatDateTime(schedule.start_datetime)} - ${formatDateTime(
				schedule.end_datetime
			)})`;
			if (schedule.was_adjusted && schedule.adjustment_reason) {
				tooltipText += `\n⚠️ ${schedule.adjustment_reason}`;
			}
			if (schedule.covered_trips?.length > 0) {
				tooltipText += `\n📌 Covered by ${schedule.covered_trips
					?.map((trip) => `#${trip.truck?.truckid} - ${trip.load_id}`)
					.join(", ")}`;
			}
			const hasForcedCovering = schedule.covered_trips?.some((t) => t.outside_schedule);
			if (hasForcedCovering) {
				tooltipText += "\n⚠️ Forced child trip (outside schedule)";
			}
			tooltipText = appendPlannedTruckTooltip(schedule, tooltipText);

			const isCovered = schedule.covered_trips?.length > 0;
			const pickupDisplayName = isCovered
				? schedule.covered_trips
						?.map((t) => t.load_id)
						.filter(Boolean)
						.join(", ") || schedule.schedule_name
				: schedule.schedule_name;
			const truckId = schedule.assigned_truck?._id || schedule.truck?._id || schedule.truck_id;
			const isClickable =
				dispatch && (isCovered ? schedule.covered_trips[0].trip_id || schedule.covered_trips[0]._id : truckId);
			schedules.push(
				<Tooltip key={`pickup-${index}`} title={tooltipText}>
					<Chip
						size="small"
						label={<>{`${getChipTruckLabel(schedule)}P: ${pickupDisplayName}`}</>}
						style={{
							backgroundColor: "#ffebee",
							color: "#c62828",
							margin: "2px",
							fontSize: "0.75rem",
							cursor: isClickable ? "pointer" : "default",
						}}
						icon={
							isCovered ? (
								hasForcedCovering ? (
									<ForcedIcon style={{ fontSize: "0.8rem" }} />
								) : (
									<CompletedIcon style={{ fontSize: "0.8rem" }} />
								)
							) : schedule.was_adjusted ? (
								<WarningIcon style={{ fontSize: "0.8rem" }} />
							) : undefined
						}
						onClick={
							isClickable
								? () => (isCovered ? openTripView(schedule.covered_trips[0]) : openTruckView(schedule))
								: undefined
						}
					/>
				</Tooltip>
			);
		});
	}

	// Add longhaul schedules
	if (chain.longhaul_schedules?.length > 0) {
		chain.longhaul_schedules.forEach((schedule, index) => {
			// Build tooltip with adjustment reason if available
			let tooltipText = `${schedule.truck?.truckid ? `#${schedule.truck?.truckid} ` : ""}${
				schedule.truck__view?.label ? `${schedule.truck__view?.label} ` : ""
			}LH: ${schedule.schedule_name} (${formatDateTime(schedule.start_datetime)} - ${formatDateTime(
				schedule.end_datetime
			)})`;
			if (schedule.was_adjusted && schedule.adjustment_reason) {
				tooltipText += `\n⚠️ ${schedule.adjustment_reason}`;
			}
			if (schedule.covered_trips?.length > 0) {
				tooltipText += `\n📌 Covered by ${schedule.covered_trips
					?.map((trip) => `#${trip.truck?.truckid} - ${trip.load_id}`)
					.join(", ")}`;
			}
			const hasForcedCoveringLH = schedule.covered_trips?.some((t) => t.outside_schedule);
			if (hasForcedCoveringLH) {
				tooltipText += "\n⚠️ Forced child trip (outside schedule)";
			}
			tooltipText = appendPlannedTruckTooltip(schedule, tooltipText);

			const isCoveredLH = schedule.covered_trips?.length > 0;
			const lhDisplayName = isCoveredLH
				? schedule.covered_trips
						?.map((t) => t.load_id)
						.filter(Boolean)
						.join(", ") || schedule.schedule_name
				: schedule.schedule_name;
			const truckIdLH = schedule.assigned_truck?._id || schedule.truck?._id || schedule.truck_id;
			const isClickableLH =
				dispatch && (isCoveredLH ? schedule.covered_trips[0].trip_id || schedule.covered_trips[0]._id : truckIdLH);
			schedules.push(
				<Tooltip key={`longhaul-${index}`} title={tooltipText}>
					<Chip
						size="small"
						label={<>{`${getChipTruckLabel(schedule)}LH: ${lhDisplayName}`}</>}
						style={{
							backgroundColor: "#f3e5f5",
							color: "#6a1b9a",
							margin: "2px",
							fontSize: "0.75rem",
							cursor: isClickableLH ? "pointer" : "default",
						}}
						icon={
							isCoveredLH ? (
								hasForcedCoveringLH ? (
									<ForcedIcon style={{ fontSize: "0.8rem" }} />
								) : (
									<CompletedIcon style={{ fontSize: "0.8rem" }} />
								)
							) : schedule.was_adjusted ? (
								<WarningIcon style={{ fontSize: "0.8rem" }} />
							) : undefined
						}
						onClick={
							isClickableLH
								? () => (isCoveredLH ? openTripView(schedule.covered_trips[0]) : openTruckView(schedule))
								: undefined
						}
					/>
				</Tooltip>
			);
		});
	}

	// Add delivery schedules
	if (chain.delivery_schedules?.length > 0) {
		chain.delivery_schedules.forEach((schedule, index) => {
			// Build tooltip with adjustment reason if available
			let tooltipText = `${schedule.truck?.truckid ? `#${schedule.truck?.truckid} ` : ""}${
				schedule.truck__view?.label ? `${schedule.truck__view?.label} ` : ""
			}D: ${schedule.schedule_name} (${formatDateTime(schedule.start_datetime)} - ${formatDateTime(
				schedule.end_datetime
			)})`;
			if (schedule.was_adjusted && schedule.adjustment_reason) {
				tooltipText += `\n⚠️ ${schedule.adjustment_reason}`;
			}
			if (schedule.covered_trips?.length > 0) {
				tooltipText += `\n📌 Covered by ${schedule.covered_trips
					?.map((trip) => `#${trip.truck?.truckid} - ${trip.load_id}`)
					.join(", ")}`;
			}
			const hasForcedCoveringD = schedule.covered_trips?.some((t) => t.outside_schedule);
			if (hasForcedCoveringD) {
				tooltipText += "\n⚠️ Forced child trip (outside schedule)";
			}
			tooltipText = appendPlannedTruckTooltip(schedule, tooltipText);

			const isCoveredD = schedule.covered_trips?.length > 0;
			const deliveryDisplayName = isCoveredD
				? schedule.covered_trips
						?.map((t) => t.load_id)
						.filter(Boolean)
						.join(", ") || schedule.schedule_name
				: schedule.schedule_name;
			const truckIdD = schedule.assigned_truck?._id || schedule.truck?._id || schedule.truck_id;
			const isClickableD =
				dispatch && (isCoveredD ? schedule.covered_trips[0].trip_id || schedule.covered_trips[0]._id : truckIdD);
			schedules.push(
				<Tooltip key={`delivery-${index}`} title={tooltipText}>
					<Chip
						size="small"
						label={<>{`${getChipTruckLabel(schedule)}D: ${deliveryDisplayName}`}</>}
						style={{
							backgroundColor: "#e8f5e8",
							color: "#2e7d32",
							margin: "2px",
							fontSize: "0.75rem",
							cursor: isClickableD ? "pointer" : "default",
						}}
						icon={
							isCoveredD ? (
								hasForcedCoveringD ? (
									<ForcedIcon style={{ fontSize: "0.8rem" }} />
								) : (
									<CompletedIcon style={{ fontSize: "0.8rem" }} />
								)
							) : schedule.was_adjusted ? (
								<WarningIcon style={{ fontSize: "0.8rem" }} />
							) : undefined
						}
						onClick={
							isClickableD
								? () => (isCoveredD ? openTripView(schedule.covered_trips[0]) : openTruckView(schedule))
								: undefined
						}
					/>
				</Tooltip>
			);
		});
	}

	// Add coverage indicator for schedule chains that have covering trips
	const coveringTripsList = chain.covering_trips || chain.coverage?.covered_by_trips || [];
	const hasOutsideSchedule = coveringTripsList.some((t) => t.outside_schedule === true);
	if (chain.chain_type !== "ACTIVE_TRIP_CHAIN" && coveringTripsList.length > 0) {
		const tripCount = coveringTripsList.length;
		schedules.push(
			<Tooltip
				key="covered"
				title={`Covered by ${tripCount} active trip(s)${
					hasOutsideSchedule ? " (includes forced child trip outside schedule)" : ""
				}`}
			>
				<Chip
					size="small"
					label={<>{`Covered${hasOutsideSchedule ? " (Forced)" : ""}`}</>}
					style={{
						backgroundColor: "#e3f2fd",
						color: "#1565c0",
						margin: "2px",
						fontSize: "0.75rem",
					}}
					icon={<CompletedIcon style={{ fontSize: "0.8rem" }} />}
				/>
			</Tooltip>
		);
		if (hasOutsideSchedule) {
			schedules.push(
				<Tooltip key="forced" title="Covered by trip(s) outside schedule — forced child trip match">
					<Chip
						size="small"
						label={<>Forced</>}
						style={{
							backgroundColor: "#fff3e0",
							color: "#e65100",
							margin: "2px",
							fontSize: "0.75rem",
						}}
						icon={<ForcedIcon style={{ fontSize: "0.8rem" }} />}
					/>
				</Tooltip>
			);
		}
	}

	return schedules;
};

const relayScheduleChainsList = (role, dispatch, readOnly = false, isMainHandler = false, dateRange = 7, history) => {
	const isInternal = authRoles.internalUsers.includes(role);
	const isCarrier = authRoles.carrierPowerUsers.includes(role) || authRoles.carrierDispatcher.includes(role);
	const isDriver = authRoles.driver.includes(role);

	return {
		urlGET: "api/profile/carriers/:carrierId/relay-schedules/chains",
		idKey: "id",
		listenEvent: ["profileRevision", "tripsRevision"],
		segmentKey: "RELAY_SCHEDULE_CHAINS",
		content: {
			pagination: {
				defaut: { size: 100 },
				enabled: true,
				resetScroll: true,
			},
			table: {
				variant: "skinny",
				multiline: true,
				orderBy: ["date", "pickup_date", "delivery_date", "origin", "destination", "urgency", "total_schedules"],
				// Darker gray background for odd calendar days (by chain date) for easier scanning by day
				getRowStyle: (item) => {
					const chainDate = item.date ? moment(item.date) : null;
					if (!chainDate || !chainDate.isValid()) return {};
					const dayOfMonth = chainDate.date();
					return dayOfMonth % 2 === 1 ? { backgroundColor: "rgba(0, 0, 0, 0.06)" } : {};
				},
			},
			order: {
				defaut: {
					key: "date",
					dir: "asc",
				},
			},
			defaults: {
				items: [
					{
						label: "All Chains",
						filters: { status: "__NOT_SELECTED__", urgency: "__NOT_SELECTED__" },
						sort: { key: "date", dir: "asc" },
					},
					{
						label: "Available",
						filters: { status: "AVAILABLE", urgency: "__NOT_SELECTED__" },
						sort: { key: "date", dir: "asc" },
					},
					{
						label: "Deferred",
						filters: { status: "DEFERRED", urgency: "__NOT_SELECTED__" },
						sort: { key: "date", dir: "asc" },
					},
					{
						label: "Urgent",
						filters: { status: "__NOT_SELECTED__", urgency: "CRITICAL,HIGH" },
						sort: { key: "urgency", dir: "asc" },
					},
					{
						label: "Completed",
						filters: { status: "COMPLETED", urgency: "__NOT_SELECTED__" },
						sort: { key: "date", dir: "asc" },
					},
					{
						label: "Has Warnings",
						filters: { status: "__NOT_SELECTED__", urgency: "__NOT_SELECTED__", hasWarnings: true },
						sort: { key: "date", dir: "asc" },
					},
				],
			},
			filters: {
				search: true,
				searchDescription: "Search by Truck ID, Schedule name, Location, or Load ID",
				default: {
					chainRange: `${moment().startOf("day").subtract(3, "days").valueOf()}__DATE_RANGE__${moment()
						.add(parseInt(dateRange), "days")
						.endOf("day")
						.valueOf()}`,
					includeActiveTrips: true,
				},
				items: [
					...(isCarrier && !isDriver && !isMainHandler
						? [
								{
									key: "truck",
									type: "autocomplete",
									label: "Truck",
									translate: "",
									field: {
										noErrorMessage: true,
										classes: {
											root: "makeStyles-field-1251",
										},
										variant: "outlined",
									},
									autocomplete: {
										provider: "smarthop",
										url: "api/profile/carriers/:carrierId/trucks",
										params: {
											options_metadata_truck: true,
											options_metadata_driver: true,
											options_active_only: true,
											label_format_show_driver: true,
										},
										preloadDefault: true,
										preloadImmedately: true,
										listenEvent: "profileRevision",
									},
								},
						  ]
						: []),
					{
						key: "chainRange",
						type: "dateRange",
						label: "Date Range",
						shortcutPriority: "long",
					},
					...(isInternal || isMainHandler
						? [
								{
									key: "carrier",
									type: "autocomplete",
									label: "Carrier",
									field: {
										noErrorMessage: true,
										classes: {},
									},
									autocomplete: {
										provider: "smarthop",
										url: "api/profile/carriers",
										preloadDefault: true,
										listenEvent: "profileRevision",
									},
								},
						  ]
						: []),
				],
			},
			items: [
				{
					key: "id",
					type: "id",
					label: "Chain ID",
					hidden: true,
				},
				/*
				{
					key: "truck_info",
					type: "component",
					builder: (item) => {
						return (
							<Box display="flex" alignItems="center">
								<TruckIcon style={{ marginRight: 8, color: '#666' }} />
								<Box>
									<Typography variant="body2" style={{ fontWeight: 500 }}>
										#{item.truck?.truckid || 'N/A'}
									</Typography>
									<Typography variant="caption" color="textSecondary">
										{item.truck__view?.name || 'Unknown'}
									</Typography>
								</Box>
							</Box>
						);
					},
					label: "Truck",
					table: { width: 120, minWidth: 100 },
					export: {
						builder: (item) => `#${item.truck?.truckid || 'N/A'} - ${item.truck?.name || 'Unknown'}`,
					},
				},
				
				{
					key: "date",
					type: "component",
					builder: (item) => {
						return <Typography variant="body2">{formatDateOnly(item.date)}</Typography>;
					},
					label: "Date",
					table: { width: 80, minWidth: 70 },
					export: {
						builder: (item) => formatDateOnly(item.date),
					},
				},*/
				{
					key: "status",
					type: "component",
					builder: (item) => {
						const status = getChainStatus(item);
						const isDeferred = status === "DEFERRED";
						const meta = isDeferred ? getDeferredReasonMeta(item.coverage_reason_code) : null;
						const deferredColor = meta?.color || DEFERRED_COLOR;
						const deferredTruckLabel = isDeferred
							? (() => {
									const lhTruck = item.longhaul_schedules?.[0]?.assigned_truck;
									if (lhTruck?.is_placeholder && lhTruck?.intended_truck_id) {
										return lhTruck.truckid !== "DEFERRED" ? lhTruck.truckid : null;
									}
									return null;
							  })()
							: null;
						const captionLabel = isDeferred
							? (meta?.label || item.coverage_reason_code?.replace(/_/g, " ") || "Deferred") +
							  (deferredTruckLabel ? ` (Truck #${deferredTruckLabel})` : "")
							: null;
						const warnings = item.coverage_warnings || [];
						const hasWarnings = warnings.length > 0;
						const warningTooltip = warnings
							.map((w) => {
								const wMeta = getWarningMeta(w.code);
								const baseLabel = wMeta ? wMeta.label : w.message || w.code;
								if (w.code === "POSSIBLE_CANCELLATION") {
									const { tooltipDetail } = formatPossibleCancellationMetadata(w);
									return tooltipDetail ? `${baseLabel} (${tooltipDetail})` : baseLabel;
								}
								return baseLabel;
							})
							.join(", ");

						return (
							<Box display="flex" flexDirection="column">
								<Box display="flex" alignItems="center">
									{getStatusIcon(status, item.coverage_reason, item.coverage_reason_code)}
									<Typography variant="body2" style={isDeferred ? { color: deferredColor, fontWeight: 500 } : {}}>
										{item.coverage_status}
									</Typography>
									{hasWarnings && !isDeferred && (
										<Tooltip title={warningTooltip}>
											<WarningIcon style={{ color: "#ff9800", fontSize: "0.9rem", marginLeft: 4 }} />
										</Tooltip>
									)}
								</Box>
								{isDeferred && captionLabel && (
									<Typography
										variant="caption"
										style={{ color: deferredColor, fontSize: "0.65rem", lineHeight: 1.2, marginTop: 2 }}
									>
										{captionLabel}
									</Typography>
								)}
								{hasWarnings &&
									warnings.map((w) => {
										const wMeta = getWarningMeta(w.code);
										const WarnIcon = wMeta?.icon || WarningIcon;
										const wColor = wMeta?.color || "#ff9800";
										let wLabel = wMeta?.label || w.code?.replace(/_/g, " ");
										let wSubLabel = null;
										let wTooltip = w.message || wLabel;
										if (w.code === "LONGHAUL_NOT_PARTNER" && w.metadata?.load_id) {
											wLabel = `${wLabel}`;
										}
										if (false && w.code === "CHILD_TRIP_OUTSIDE_SCHEDULE" && w.metadata?.reasons?.length > 0) {
											const reasonLabels = w.metadata.reasons
												.map((r) => OUTSIDE_SCHEDULE_REASON_LABELS[r] || r)
												.join(", ");
											const loadSuffix =
												w.metadata?.displaced_load_ids?.length > 0
													? ` (${w.metadata.displaced_load_ids.join(", ")})`
													: "";
											wLabel = `Outside: ${reasonLabels}${loadSuffix}`;
										}
										if (w.code === "POSSIBLE_CANCELLATION") {
											const { subLabel, tooltipDetail } = formatPossibleCancellationMetadata(w);
											wSubLabel = subLabel;
											if (tooltipDetail) {
												wTooltip = w.message ? `${w.message}\n${tooltipDetail}` : `${wLabel} — ${tooltipDetail}`;
											}
										}
										return (
											<Tooltip key={w.code} title={wTooltip}>
												<Box display="flex" flexDirection="column" style={{ marginTop: 3 }}>
													<Box display="flex" alignItems="center">
														<WarnIcon style={{ color: wColor, fontSize: "0.85rem", marginRight: 3 }} />
														<Typography
															variant="caption"
															style={{ color: wColor, fontSize: "0.7rem", lineHeight: 1.2 }}
														>
															{wLabel}
														</Typography>
													</Box>
													{wSubLabel && (
														<Typography
															variant="caption"
															style={{
																color: wColor,
																fontSize: "0.65rem",
																lineHeight: 1.2,
																marginLeft: 14,
																fontStyle: "italic",
															}}
														>
															{wSubLabel}
														</Typography>
													)}
												</Box>
											</Tooltip>
										);
									})}
								{item.unavailable_trucks?.length > 0 && (
									<Tooltip title={item.unavailable_trucks.map((t) => `#${t.truckid || t._id}`).join(", ")}>
										<Box display="flex" alignItems="center" style={{ marginTop: 3 }}>
											<EventBusyIcon style={{ color: "#7b1fa2", fontSize: "0.85rem", marginRight: 3 }} />
											<Typography variant="caption" style={{ color: "#7b1fa2", fontSize: "0.7rem", lineHeight: 1.2 }}>
												{`Unavailable: ${item.unavailable_trucks.map((t) => `#${t.truckid || t._id}`).join(", ")}`}
											</Typography>
										</Box>
									</Tooltip>
								)}
							</Box>
						);
					},
					label: "Status",
					table: { width: 140, minWidth: 100 },
					export: {
						builder: (item) => {
							const status = getChainStatus(item);
							const suffix = item.urgency && item.urgency !== "LOW" ? ` (${item.urgency})` : "";
							const deferredMeta = status === "DEFERRED" ? getDeferredReasonMeta(item.coverage_reason_code) : null;
							const reasonLabel = deferredMeta?.label || item.coverage_reason_code?.replace(/_/g, " ") || "";
							let reason = status === "DEFERRED" && reasonLabel ? ` - ${reasonLabel}` : "";
							if (status === "DEFERRED") {
								const lhTruck = item.longhaul_schedules?.[0]?.assigned_truck;
								if (lhTruck?.is_placeholder && lhTruck?.intended_truck_id && lhTruck.truckid !== "DEFERRED") {
									reason += ` (Truck #${lhTruck.truckid})`;
								}
							}
							const warnings = (item.coverage_warnings || [])
								.map((w) => {
									if (w.code === "POSSIBLE_CANCELLATION") {
										const { tooltipDetail } = formatPossibleCancellationMetadata(w);
										return tooltipDetail ? `${w.code}(${tooltipDetail})` : w.code;
									}
									const lid = w.metadata?.load_id;
									return lid ? `${w.code}(${lid})` : w.code;
								})
								.join(", ");
							const warningStr = warnings ? ` [Warnings: ${warnings}]` : "";
							return `${status}${suffix}${reason}${warningStr}`;
						},
					},
				},
				{
					key: "truck__view",
					type: "truck__view",
					label: "Truck",
					viewConfig: {
						readOnly,
						showPhone: false,
						showTeam: true,
					},
					table: { width: 160, minWidth: 100 },
				},
				{
					key: "pickup_date",
					type: "component",
					builder: (item) => {
						// Find the earliest pickup schedule start time
						let earliestPickup = null;
						let latestPickup = null;
						if (item.pickup_schedules && item.pickup_schedules.length > 0) {
							item.pickup_schedules.forEach((schedule) => {
								const startTime = moment(schedule.start_datetime);
								const endTime = moment(schedule.end_datetime);
								if (!earliestPickup || startTime.isBefore(earliestPickup)) {
									earliestPickup = startTime;
								}
								if (!latestPickup || endTime.isAfter(latestPickup)) {
									latestPickup = endTime;
								}
							});
						}
						return (
							<Typography variant="body2">
								{earliestPickup ? formatDateTime(earliestPickup) : "N/A"}
								{earliestPickup && latestPickup && !earliestPickup.isSame(latestPickup)
									? ` - ${formatTime(latestPickup)}`
									: ""}
							</Typography>
						);
					},
					label: "Pickup Date",
					table: { width: 150, minWidth: 120 },
					export: {
						builder: (item) => {
							let earliestPickup = null;
							let latestPickup = null;
							if (item.pickup_schedules && item.pickup_schedules.length > 0) {
								item.pickup_schedules.forEach((schedule) => {
									const startTime = moment(schedule.start_datetime);
									const endTime = moment(schedule.end_datetime);
									if (!earliestPickup || startTime.isBefore(earliestPickup)) {
										earliestPickup = startTime;
									}
									if (!latestPickup || endTime.isAfter(latestPickup)) {
										latestPickup = endTime;
									}
								});
							}
							return earliestPickup ? formatDateTime(earliestPickup) : "N/A";
						},
					},
				},
				{
					key: "origin",
					type: "component",
					builder: (item) => {
						const origins = [];
						// Collect all origins from pickup
						[...(item.pickup_schedules || [])].forEach((schedule) => {
							if (schedule.origin_yard) {
								if (schedule.origin_yard.search_city && !schedule.was_adjusted && !schedule.covered_trips?.length > 0) {
									origins.push(`${schedule.origin_yard.search_city}, ${schedule.origin_yard.search_state}`);
								} else {
									origins.push(`${schedule.origin_yard.city}, ${schedule.origin_yard.state}`);
								}
							}
						});
						const uniqueOrigins = [...new Set(origins)];
						return (
							<Typography variant="body2">{uniqueOrigins.length > 0 ? uniqueOrigins.join(", ") : "N/A"}</Typography>
						);
					},
					label: "Origin",
					table: { width: 100, minWidth: 80 },
					export: {
						builder: (item) => {
							const origins = [];
							[...(item.pickup_schedules || [])].forEach((schedule) => {
								if (schedule.origin_yard) {
									if (
										schedule.origin_yard.search_city &&
										!schedule.was_adjusted &&
										!schedule.covered_trips?.length > 0
									) {
										origins.push(`${schedule.origin_yard.search_city}, ${schedule.origin_yard.search_state}`);
									} else {
										origins.push(`${schedule.origin_yard.city}, ${schedule.origin_yard.state}`);
									}
								}
							});
							return [...new Set(origins)].join(", ") || "N/A";
						},
					},
				},
				{
					key: "delivery_date",
					type: "component",
					builder: (item) => {
						// Find the latest delivery schedule end time
						let latestDelivery = null;
						let earliestDelivery = null;
						if (item.delivery_schedules && item.delivery_schedules.length > 0) {
							item.delivery_schedules.forEach((schedule) => {
								const endTime = moment(schedule.end_datetime);
								const startTime = moment(schedule.start_datetime);
								if (!earliestDelivery || startTime.isBefore(earliestDelivery)) {
									earliestDelivery = startTime;
								}
								if (!latestDelivery || endTime.isAfter(latestDelivery)) {
									latestDelivery = endTime;
								}
							});
						}
						return (
							<Typography variant="body2">
								{earliestDelivery ? formatDateTime(earliestDelivery) : "N/A"}
								{earliestDelivery && latestDelivery && !earliestDelivery.isSame(latestDelivery)
									? ` - ${formatTime(latestDelivery)}`
									: ""}
							</Typography>
						);
					},
					label: "Delivery Date",
					table: { width: 150, minWidth: 120 },
					export: {
						builder: (item) => {
							let latestDelivery = null;
							let earliestDelivery = null;
							if (item.delivery_schedules && item.delivery_schedules.length > 0) {
								item.delivery_schedules.forEach((schedule) => {
									const endTime = moment(schedule.end_datetime);
									const startTime = moment(schedule.start_datetime);
									if (!earliestDelivery || startTime.isBefore(earliestDelivery)) {
										earliestDelivery = startTime;
									}
									if (!latestDelivery || endTime.isAfter(latestDelivery)) {
										latestDelivery = endTime;
									}
								});
							}
							return latestDelivery ? formatDateTime(latestDelivery) : "N/A";
						},
					},
				},
				{
					key: "destination",
					type: "component",
					builder: (item) => {
						const destinations = [];
						// Collect all destinations from longhaul and delivery schedules
						[...(item.delivery_schedules || [])].forEach((schedule) => {
							if (schedule.destination_yard) {
								if (
									schedule.destination_yard.search_city &&
									!schedule.was_adjusted &&
									!schedule.covered_trips?.length > 0
								) {
									destinations.push(
										`${schedule.destination_yard.search_city}, ${schedule.destination_yard.search_state}`
									);
								} else {
									destinations.push(`${schedule.destination_yard.city}, ${schedule.destination_yard.state}`);
								}
							}
						});
						const uniqueDestinations = [...new Set(destinations)];
						return (
							<Typography variant="body2">
								{uniqueDestinations.length > 0 ? uniqueDestinations.join(", ") : "N/A"}
							</Typography>
						);
					},
					label: "Destination",
					table: { width: 100, minWidth: 80 },
					export: {
						builder: (item) => {
							const destinations = [];
							[...(item.delivery_schedules || [])].forEach((schedule) => {
								if (schedule.destination_yard) {
									if (
										schedule.destination_yard.search_city &&
										!schedule.was_adjusted &&
										!schedule.covered_trips?.length > 0
									) {
										destinations.push(
											`${schedule.destination_yard.search_city}, ${schedule.destination_yard.search_state}`
										);
									} else {
										destinations.push(`${schedule.destination_yard.city}, ${schedule.destination_yard.state}`);
									}
								}
							});
							return [...new Set(destinations)].join(", ") || "N/A";
						},
					},
				},

				{
					key: "schedule_chain",
					type: "component",
					builder: (item) => {
						const chainCarrierId = item.carrier || item.pickup_schedules?.[0]?.carrier_id;
						return (
							<Box flexWrap="wrap" alignItems="center">
								{renderScheduleChips(item, dispatch, chainCarrierId)}
							</Box>
						);
					},
					label: "Schedule Chain",
					table: { width: 320, minWidth: 270 },
					export: {
						builder: (item) => {
							const types = [];
							if (item.pickup_schedules?.length > 0) types.push(`P:${item.pickup_schedules.length}`);
							if (item.longhaul_schedules?.length > 0) types.push(`LH:${item.longhaul_schedules.length}`);
							if (item.delivery_schedules?.length > 0) types.push(`D:${item.delivery_schedules.length}`);
							return types.join(" | ");
						},
					},
				},
				{
					key: "covering_trips",
					type: "trips__view",
					label: "Booked Trips",

					viewConfig: {
						readOnly,
						showBroker: true,
					},
					builder: (item) => {
						// Check if this is an active trip chain with covering trips directly
						if (item.covering_trips && item.covering_trips.length > 0) {
							return item.covering_trips.map((trip) => ({
								label: `# ${trip.load_id}`,
								value: trip.trip_id,
								metadata: {
									carrier: trip?.carrier || "",
									load_id: trip.load_id,
									broker: trip?.broker || "",
									pickup_address: trip?.pickup_address,
									delivery_address: trip?.delivery_address,
									status: trip?.status,
									relay_type: trip?.relay_type,
								},
							}));
						} else {
							return [];
						}
					},
					export: {
						builder: (item) => {
							return item.covering_trips.length > 0
								? item.covering_trips.map((trip) => `# ${trip.load_id}`).join(", ")
								: "None";
						},
					},
					table: { width: 100, minWidth: 80 },
				},
				{
					key: "menu",
					type: "menu",
					label: "Actions",
					table: {
						width: 80,
						sticky: true,
					},
					items: [
						{
							key: "view_chain",
							label: "View Chain",
							icon: "visibility",
							dialogBuilder: (item) => {
								// Convert the chain item to relay chain format for the view
								const relayChain = {
									chainId: item.id,
									date: item.date,
									pickup: item.pickup_schedules?.[0] || null,
									longhaul: item.longhaul_schedules?.[0] || null,
									delivery: item.delivery_schedules?.[0] || null,
									// Include all schedules for complete view
									pickup_schedules: item.pickup_schedules,
									longhaul_schedules: item.longhaul_schedules,
									delivery_schedules: item.delivery_schedules,
									covering_trips: item.covering_trips,
									chain_type: item.chain_type,
									urgency: item.urgency,
									coverage_status: item.coverage_status,
									truck__view: item.truck__view,
									truck: item.truck,
								};

								return {
									viewId: "RELAY_SCHEDULE_CHAIN_VIEW",
									dataIds: {
										relayChain: relayChain,
										carrierId: item.carrier || item.pickup_schedules?.[0]?.carrier_id,
										targetDate: item.date,
									},
								};
							},
						},

						{
							key: "search_loads",
							label: "Search for Loads",
							icon: "search",
							disabled: (item) => {
								// Disable search for completed/active chains and deferred chains
								const status = getChainStatus(item);
								return status === "COMPLETED";
							},
							dispatchAction: (item, dataIds, dispatch, history, snackbar) => {
								handleSearchForLoads(item, history, dispatch);
							},
						},
						{
							variant: "divider",
						},
						{
							key: "start_agent",
							label: "Start Agent",
							icon: "play_arrow",
							disabled: (item) => {
								// Disable for completed/active chains, deferred chains, or chains without truck
								const status = getChainStatus(item);
								return status === "COMPLETED" || !item.truck || !item.truck._id;
							},
							dispatchAction: async (item, dataIds, dispatch, history, snackbar) => {
								await handleStartAgent(item, dispatch, snackbar);
							},
						},
						{
							key: "update_truck_plan",
							label: "Update Truck Plan Only",
							icon: "local_shipping",
							disabled: (item) => {
								// Disable for completed/active chains, deferred chains, or chains without truck
								const status = getChainStatus(item);
								return status === "COMPLETED" || !item.truck || !item.truck._id;
							},
							dialogBuilder: (item) => {
								const truckLabel = item.truck__view?.label || `Truck #${item.truck?.truckid}` || "Unknown";
								const originLocations = [
									...(item.pickup_schedules || []).map((s) => {
										if (s.origin_yard?.search_city && !s.was_adjusted && !s.covered_trips?.length) {
											return `${s.origin_yard.search_city}, ${s.origin_yard.search_state}`;
										}
										return s.origin_yard ? `${s.origin_yard.city}, ${s.origin_yard.state}` : null;
									}),
								]
									.filter(Boolean)
									.filter((v, i, a) => a.indexOf(v) === i);
								const origin = originLocations[0] || "N/A";

								const destinationLocations = [
									...(item.delivery_schedules || []).map((s) => {
										if (s.destination_yard?.search_city && !s.was_adjusted && !s.covered_trips?.length) {
											return `${s.destination_yard.search_city}, ${s.destination_yard.search_state}`;
										}
										return s.destination_yard ? `${s.destination_yard.city}, ${s.destination_yard.state}` : null;
									}),
								]
									.filter(Boolean)
									.filter((v, i, a) => a.indexOf(v) === i);
								const destination = destinationLocations[0] || "N/A";

								return {
									viewId: "CONFIRM_DIALOG",
									mode: "CREATE",
									dataIds: {
										title: "Update Truck Plan",
										message: `This will update the truck plan based on this relay schedule chain.\n\nTruck: ${truckLabel}\nChain: ${item.id}\nOrigin: ${origin} \nDestination: ${destination}\n\nThe truck plan will be configured for automatic load booking with relay-specific settings.`,
										confirmLabel: "Update Plan",
										url: `api/profile/carriers/${item.carrier}/relay-schedules/chains/update-truck-plan`,
										method: "POST",
										body: { data: item },
										successMessage: "Truck plan updated successfully",
										errorMessage: "Failed to update truck plan",
										listenEvent: "profileRevision",
									},
								};
							},
						},
						{
							variant: "divider",
						},
						{
							key: "manual_defer",
							label: "Manual Defer",
							icon: "pause_circle_filled",
							disabled: (item) => {
								// Only allow when chain is AVAILABLE (not deferred, completed, or urgent)
								const status = getChainStatus(item);
								if (status !== "AVAILABLE" && status !== "URGENT") return true;
								if (!item.longhaul_schedules?.length) return true;
								if (!item.longhaul_schedules[0]?.schedule_id) return true;
								// Need an assigned truck from the longhaul schedule or the chain-level truck
								const lhSchedule = item.longhaul_schedules[0];
								const longhaulTruckId =
									lhSchedule?.assigned_truck?._id || lhSchedule?.truck?._id || lhSchedule?.truck_id || item.truck?._id;
								if (!longhaulTruckId) return true;
								return false;
							},
							dialogBuilder: (item) => {
								const carrierId = item.carrier || item.pickup_schedules?.[0]?.carrier_id;
								const lhSchedule = item.longhaul_schedules?.[0];
								const scheduleId = lhSchedule?.schedule_id;
								// Use the assigned truck from the longhaul schedule instance (not the booking truck)
								const lhTruck = lhSchedule?.assigned_truck;
								const longhaulTruckId =
									lhTruck?._id || lhSchedule?.truck?._id || lhSchedule?.truck_id || item.truck?._id;
								const truckLabel = lhTruck?.truckid
									? `Truck #${lhTruck.truckid}${lhTruck.name ? ` - ${lhTruck.name}` : ""}`
									: lhSchedule?.truck?.truckid
									? `Truck #${lhSchedule.truck.truckid}`
									: item.truck__view?.label || "Unknown";
								const chainDate = item.date;

								return {
									viewId: "CONFIRM_DIALOG",
									mode: "CREATE",
									dataIds: {
										title: "Manual Defer Chain",
										message: `This will manually defer this relay chain for ${chainDate}.\n\nLonghaul Truck: ${truckLabel}\nDate: ${chainDate}\nSchedule: ${
											lhSchedule?.schedule_name || "N/A"
										}\n\nThe longhaul truck will be marked as deferred for this date and will not be assigned to loads.`,
										confirmLabel: "Defer",
										url: `api/relay/carriers/${carrierId}/relay-schedules/${scheduleId}/manual-deferrals`,
										method: "POST",
										body: {
											date: chainDate,
											truck: longhaulTruckId,
											reason_code: "MANUAL_DEFERRED",
											reason: "Manually deferred from chains list",
										},
										successMessage: "Chain manually deferred successfully",
										errorMessage: "Failed to defer chain",
										listenEvent: "profileRevision",
									},
								};
							},
						},
						{
							key: "remove_deferral",
							label: "Remove Deferral",
							icon: "play_circle_filled",
							disabled: (item) => {
								// Only allow removing deferral from manually deferred chains
								if (item.coverage_reason_code !== "MANUAL_DEFERRED") return true;
								if (!item.longhaul_schedules?.length) return true;
								if (!item.longhaul_schedules[0]?.schedule_id) return true;
								// Need the intended truck from the longhaul schedule
								const lhSchedule = item.longhaul_schedules[0];
								const longhaulTruckId =
									lhSchedule?.assigned_truck?._id || lhSchedule?.truck?._id || lhSchedule?.truck_id || item.truck?._id;
								if (!longhaulTruckId) return true;
								return false;
							},
							dialogBuilder: (item) => {
								const carrierId = item.carrier || item.pickup_schedules?.[0]?.carrier_id;
								const lhSchedule = item.longhaul_schedules?.[0];
								const scheduleId = lhSchedule?.schedule_id;
								// Use the assigned/intended truck from the longhaul schedule instance (not the booking truck)
								const lhTruck = lhSchedule?.assigned_truck;
								const longhaulTruckId =
									lhTruck?._id || lhSchedule?.truck?._id || lhSchedule?.truck_id || item.truck?._id;
								const truckLabel = lhTruck?.truckid
									? `Truck #${lhTruck.truckid}${lhTruck.name ? ` - ${lhTruck.name}` : ""}`
									: lhSchedule?.truck?.truckid
									? `Truck #${lhSchedule.truck.truckid}`
									: item.truck__view?.label || "Unknown";
								const chainDate = item.date;

								return {
									viewId: "CONFIRM_DIALOG",
									mode: "CREATE",
									dataIds: {
										title: "Remove Manual Deferral",
										message: `This will remove the manual deferral for this relay chain.\n\nLonghaul Truck: ${truckLabel}\nDate: ${chainDate}\nSchedule: ${
											lhSchedule?.schedule_name || "N/A"
										}\n\nThe longhaul truck will be available again for load assignment on this date.`,
										confirmLabel: "Remove Deferral",
										url: `api/relay/carriers/${carrierId}/relay-schedules/${scheduleId}/manual-deferrals/remove-by-date-truck`,
										method: "POST",
										body: {
											date: chainDate,
											truck: longhaulTruckId,
										},
										successMessage: "Deferral removed successfully",
										errorMessage: "Failed to remove deferral",
										listenEvent: "profileRevision",
									},
								};
							},
						},
						{
							variant: "divider",
						},
						{
							key: "fix_warnings",
							label: "Fix Chain Warnings",
							icon: "build",
							disabled: (item) => {
								const warnings = item.coverage_warnings || [];
								if (warnings.length === 0) return true;
								return false;
							},
							dispatchAction: async (item, dataIds, dispatch, history, snackbar) => {
								const carrierId = item.carrier || item.pickup_schedules?.[0]?.carrier_id;
								const chainId = item.id;
								if (!carrierId || !chainId) {
									showSnackbar(snackbar, "Missing carrier or chain information", "error");
									return;
								}
								dispatch(
									openFormDialog({
										viewId: "RELAY_CHAIN_FIX_WARNINGS_VIEW",
										dataIds: { carrierId, chainId, chain: item },
									})
								);
							},
						},
					],
				},
				// Hidden fields for export
				{
					key: "total_schedules",
					type: "text",
					label: "Total Schedules",
					hidden: true,
					builder: (item) => item.total_schedules,
					export: { builder: (item) => item.total_schedules },
				},
			],
			export: {
				fileName: "Relay Schedule Chains",
			},
		},
	};
};

export default relayScheduleChainsList;
